import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { Subject } from "rxjs";

import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { HttpService } from "../http/http";
import { catchError, last, map, takeUntil } from "rxjs/operators";
import { Auth } from "../auth";
import { environment } from "../../../../environments/environment";

export class OvhUploadFile implements IUploadFile {
  protected _cancelUploadRequest: Subject<void> = new Subject<void>();
  private _started = false;
  private _duringAbort = false;
  private _fileUrl: string;
  private _uploadParts: Array<UploadPart>;
  private _lastFinishedPartIndex = 0;

  constructor(
    private _uploadId: string,
    private _file: File,
    private _storageEndpointUrl: string,
    private _containerName: string,
    private _objectKey: string,
    private _chunkSizeInBytes: number,
    private _customData: any,
    private notifyPromise: (objectKey: string) => Promise<any>,
    private _createdDate: Date,
    private _httpClient: HttpClient,
    private _httpService: HttpService,
    private _auth: Auth,
  ) {
    this._abortedChange = new Subject<void>();
    this._isErrorChange = new Subject<any>();
    this._progressChange = new Subject<number>();
    this._isCompleteChange = new Subject<any>();
    // this._chunkSizeInBytes = 1048576;
    // this._chunkSizeInBytes = 1024 * 1000;
    this._objectKey = encodeURI(_objectKey);
    this._fileUrl = `${_storageEndpointUrl}/${_containerName}/${this._objectKey}`;
  }

  private _isComplete: boolean;

  get isComplete(): boolean {
    return this._isComplete;
  }

  private _abortedChange: Subject<void>;

  get abortedChange(): Subject<void> {
    return this._abortedChange;
  }

  private _isAborted: boolean;

  get isAborted(): boolean {
    return this._isAborted;
  }

  private _isCompleteChange: Subject<any>;

  get isCompleteChange(): Subject<any> {
    return this._isCompleteChange;
  }

  private _isError: boolean;

  get isError(): boolean {
    return this._isError;
  }

  private _isErrorChange: Subject<void>;

  get isErrorChange(): Subject<void> {
    return this._isErrorChange;
  }

  private _progress: number;

  get progress(): number {
    return this._progress;
  }

  private _progressChange: Subject<number>;

  get progressChange(): Subject<number> {
    return this._progressChange;
  }

  get customData(): any {
    return this._customData;
  }

  get id() {
    return this._uploadId;
  }

  get file() {
    return this._file;
  }

  get createdDate() {
    return this._createdDate;
  }

  async start() {
    if (this._started) {
      return;
    }

    this._started = true;
    this.setProgress(0);

    if (!this._uploadParts) {
      this._uploadParts = await this.createUploadPartsArray();
    }

    const segmentsMetaDatas: Array<SegmentMetaData> = [];

    for (
      let i = this._lastFinishedPartIndex;
      i < this._uploadParts.length;
      i++
    ) {
      const part = this._uploadParts[i];

      // @ts-ignore
      // tslint:disable-next-line:max-line-length
      const segmentMetaData: SegmentMetaData =
        await this._instansiateSignedHttpRequest(
          part.pathData.segmentUrl,
          part.blob,
        ).then((uploadSubscriber) => {
          return new Promise((resolve, reject) => {
            uploadSubscriber.subscribe((ev: HttpEvent<string>) => {
              if (ev.type === HttpEventType.UploadProgress) {
                const percentDone =
                  (i * this._chunkSizeInBytes + ev.loaded) / this._file.size;
                this.setProgress(percentDone);
                console.log(`File is ${percentDone}% uploaded.`);
              } else if (ev.type === HttpEventType.Response) {
                this._lastFinishedPartIndex = i;
                resolve(
                  new SegmentMetaData(
                    part.pathData.internalContainerPath,
                    ev.headers.get("etag"),
                    part.blob.size,
                  ),
                );
                console.log("File is completely uploaded!");
              }
            });
          });
        });

      segmentsMetaDatas.push(segmentMetaData);
    }

    if (this._uploadParts.length > 1) {
      await this.createLargeObjectManifestFile(segmentsMetaDatas);
    }

    console.log("setComplete ");
    this.setComplete(this._fileUrl);
  }

  async abort() {
    if (this._duringAbort) {
      return;
    }
    this._duringAbort = true;
    console.log(
      `OvhUploadFile-> Abort uploading ${this._fileUrl} . Deleting artifacts...`,
    );
    this._cancelUploadRequest.next();
    this._cancelUploadRequest.complete();

    this._cancelUploadRequest = new Subject<void>();

    for (let i = 0; i < this._lastFinishedPartIndex; i++) {
      const part = this._uploadParts[i];
      console.log(`OvhUploadFile-> Deleting ${part.pathData.segmentUrl}`);
      await this._instansiateSignedHttpRequest(
        part.pathData.segmentUrl,
        null,
        null,
        "DELETE",
      ).then((deleteSubscriber) => {
        return new Promise((resolve) => {
          deleteSubscriber
            .pipe(last((resp) => resp instanceof HttpResponse))
            .subscribe(resolve);
        });
      });
      const progess =
        ((this._lastFinishedPartIndex - i - 1) * this._chunkSizeInBytes) /
        this._file.size;
      this.setProgress(progess);
      console.log(
        `OvhUploadFile-> Finished Deleting artifacts of ${this._fileUrl}`,
      );
    }

    this._lastFinishedPartIndex = 0;

    this.setAbort();
  }

  async retry() {
    console.log(`OvhUploadFile-> Retry uploading ${this._fileUrl}`);
    this._started = false;
    return this.start();
  }

  private async createLargeObjectManifestFile(
    segmentsData: Array<SegmentMetaData>,
  ) {
    console.log(
      "creating a manifest file, path ",
      this.getPathToSegmentsFolder(),
    );
    return this._httpService.post("Ovh/CreateDLO", {
      containerName: this._containerName,
      objectKey: this._objectKey,
      prefix: this.getPathToSegmentsFolder(),
      contentType: this._file.type,
      segmentsData,
      type: 1,
    });
  }

  // tslint:disable-next-line:max-line-length
  private async _instansiateSignedHttpRequest(
    fileUrl: string,
    blob: Blob,
    httpHeaders: HttpHeaders = null,
    httpMethod: string = "PUT",
  ) {
    const token = this._auth.token.tokenType + " " + this._auth.token.token;
    let signedUrl = await this._httpClient
      .request(
        new HttpRequest(
          "POST",
          environment.ENDPOINT + "/api/Ovh/SignUrl",
          {
            method: httpMethod,
            urlToSign: fileUrl,
          },
          {
            headers: new HttpHeaders({ Authorization: token }),
          },
        ),
      )
      .pipe(
        takeUntil(this._cancelUploadRequest),
        last(
          (resp) =>
            resp instanceof HttpResponse || resp instanceof HttpErrorResponse,
        ),
        map((resp: any) => {
          if (resp.body) {
            return resp.body.message;
          }

          return resp;
        }),
      )
      .toPromise()
      .catch((err) => {});
    if (!signedUrl) {
      return;
    }

    signedUrl = encodeURI(signedUrl);
    console.log(
      "OvhUploadFile->_uploadToOvhCloudStorageMethod->created signed url: ",
      signedUrl,
    );
    const req = new HttpRequest(httpMethod, signedUrl, blob, {
      reportProgress: true,
      headers: httpHeaders,
    });
    this._cancelUploadRequest = new Subject<void>();

    return this._httpClient.request(req).pipe(
      takeUntil(this._cancelUploadRequest),
      catchError((err, observer) => {
        this.setError(err.message);
        return err;
      }),
    );
  }

  private setComplete(notifyServerResult: any) {
    this._isComplete = true;
    this.notifyPromise(notifyServerResult);
    this._isCompleteChange.next(notifyServerResult);
  }

  private setError(err) {
    this._isError = true;
    this._isErrorChange.next(err);
  }

  private setProgress(val) {
    this._progress = val;
    this._progressChange.next(val);
  }

  private setAbort() {
    this._isAborted = true;
    this._abortedChange.next();
  }

  private createUploadPartsArray(): Promise<Array<UploadPart>> {
    return new Promise((resolve, reject) => {
      const result = [];
      if (this._file.size <= this._chunkSizeInBytes) {
        result.push(
          new UploadPart(
            {
              segmentUrl: this._fileUrl,
              internalContainerPath:
                this._containerName + "/" + this._objectKey,
            },
            this._file,
          ),
        );
      } else {
        for (
          let i = 0, chunkNum = 0;
          i < this._file.size;
          i += this._chunkSizeInBytes, chunkNum++
        ) {
          const bytesSlice = this._file.slice(i, i + this._chunkSizeInBytes);
          const blob = new Blob([bytesSlice], {
            type: "application/octet-stream",
          });

          const segmentPathData: ISegmentPath =
            this.generateSegmentPathData(chunkNum);

          result.push(new UploadPart(segmentPathData, blob));
        }
      }

      resolve(result);
    });
  }

  private generateSegmentPathData(num: number): ISegmentPath {
    const segmentObjectPath = `${this.getPathToSegmentsFolder()}/${num.toString().padStart(this._file.size.toString().length, "0")}`;
    // tslint:disable-next-line:max-line-length
    return {
      segmentUrl: `${this._storageEndpointUrl}/${segmentObjectPath}`,
      internalContainerPath: segmentObjectPath,
    };
  }

  private getPathToSegmentsFolder(): string {
    return `${this._containerName}/${this._objectKey}_app-upload-segments`;
  }
}

interface ISegmentPath {
  internalContainerPath: string;
  segmentUrl: string;
}

class SegmentMetaData {
  constructor(
    public path: string,
    public etag: string,
    public size_bytes: number,
  ) {}
}
class UploadPart {
  constructor(
    public pathData: ISegmentPath,
    public blob: Blob,
  ) {}
}
